import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Shield, Loader2, Check, Eye, EyeOff, Mail, LockKeyhole, ArrowLeft, KeyRound } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "../components/ui/input-otp";
import { toast } from "sonner";

export const ForgotPassword = () => {
  const navigate = useNavigate();
  const { forgotPassword, resetPassword } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState({
    email: "",
    otp: "",
    newPassword: "",
    confirmPassword: "",
  });

  const handleSendOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      await forgotPassword(formData.email);
      setStep(2);
      toast.success("OTP sent to your email");
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError("");

    if (formData.newPassword !== formData.confirmPassword) {
      setError("Passwords don't match");
      return;
    }

    if (formData.newPassword.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }

    setLoading(true);

    try {
      await resetPassword(formData.email, formData.otp, formData.newPassword);
      setSuccess(true);
      toast.success("Password reset successfully!");
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="w-full max-w-[450px] bg-white rounded-[3rem] shadow-2xl p-10 text-center border border-slate-200/60 animate-in zoom-in-95 duration-300">
          <div className="w-20 h-20 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-green-500" />
          </div>
          <h2 className="text-3xl font-black text-slate-900 tracking-tight mb-2">Success!</h2>
          <p className="text-slate-500 mb-8 font-medium">Your vault access has been restored with your new password.</p>
          <Button
            onClick={() => navigate("/login")}
            className="w-full h-14 bg-slate-900 text-white rounded-2xl font-bold shadow-xl hover:bg-slate-800 transition-all"
            data-testid="go-to-login-btn"
          >
            Return to Login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-0 md:p-6 font-sans text-slate-900">
      <div className="w-full max-w-[450px] h-screen md:h-auto md:min-h-[650px] bg-white md:rounded-[3rem] shadow-2xl overflow-hidden flex flex-col relative border border-slate-200/60">
        
        {/* Header */}
        <header className="px-10 pt-16 pb-8 z-10 text-center">
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 rounded-[1.5rem] bg-slate-900 flex items-center justify-center shadow-xl">
              <KeyRound className="w-8 h-8 text-white" />
            </div>
          </div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight leading-tight">
            {step === 1 ? "Recovery" : "New Password"}
          </h1>
          <p className="text-slate-400 text-sm mt-2 font-medium">
            {step === 1
              ? "Verify your identity to reset access"
              : "Create a strong new password for your vault"}
          </p>
        </header>

        <div className="flex-1 px-10 z-10">
          {error && (
            <Alert variant="destructive" className="mb-6 rounded-2xl bg-red-50 border-none text-red-600 animate-in slide-in-from-top-2">
              <AlertDescription className="text-sm font-bold flex items-center gap-2">
                <LockKeyhole size={14} /> {error}
              </AlertDescription>
            </Alert>
          )}

          {step === 1 ? (
            <form onSubmit={handleSendOTP} className="space-y-6">
              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-[11px] font-bold text-slate-500 ml-1 uppercase tracking-wider">
                  Associated Email
                </Label>
                <div className="relative">
                  <Input
                    id="email"
                    type="email"
                    className="h-14 rounded-2xl bg-slate-100 border border-slate-200 text-slate-900 text-lg font-medium placeholder:text-slate-300"
                    placeholder="name@example.com"
                    value={formData.email}
                    onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                    required
                    data-testid="email-input"
                  />
                  <Mail className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-300" size={20} />
                </div>
              </div>
              <Button
                type="submit"
                className="w-full h-14 bg-slate-900 text-white rounded-2xl font-bold shadow-xl hover:bg-slate-800 transition-all mt-4"
                disabled={loading}
                data-testid="send-otp-btn"
              >
                {loading ? <Loader2 className="animate-spin" /> : "Send Reset Code"}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleResetPassword} className="space-y-6 animate-in fade-in slide-in-from-right-4">
              <div className="space-y-1.5">
                <Label className="text-[11px] font-bold text-slate-500 ml-1 uppercase tracking-wider text-center block">
                  Verification Code
                </Label>
                <div className="flex justify-center py-2">
                  <InputOTP
                    maxLength={6}
                    value={formData.otp}
                    onChange={(value) => setFormData((prev) => ({ ...prev, otp: value }))}
                    data-testid="otp-input"
                  >
                    <InputOTPGroup className="gap-2">
                      {[0, 1, 2, 3, 4, 5].map((index) => (
                        <InputOTPSlot 
                          key={index} 
                          index={index} 
                          className="w-11 h-14 rounded-xl border-slate-200 bg-slate-50 text-slate-900 font-bold text-xl"
                        />
                      ))}
                    </InputOTPGroup>
                  </InputOTP>
                </div>
              </div>

              <div className="space-y-4">
                <div className="space-y-1.5">
                  <Label className="text-[11px] font-bold text-slate-500 ml-1 uppercase tracking-wider">New Password</Label>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      className="h-14 rounded-2xl bg-slate-100 border border-slate-200 text-slate-900 text-lg"
                      placeholder="••••••••"
                      value={formData.newPassword}
                      onChange={(e) => setFormData((prev) => ({ ...prev, newPassword: e.target.value }))}
                      required
                      data-testid="new-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-[11px] font-bold text-slate-500 ml-1 uppercase tracking-wider">Confirm Password</Label>
                  <Input
                    type={showPassword ? "text" : "password"}
                    className="h-14 rounded-2xl bg-slate-100 border border-slate-200 text-slate-900 text-lg"
                    placeholder="••••••••"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData((prev) => ({ ...prev, confirmPassword: e.target.value }))}
                    required
                    data-testid="confirm-password-input"
                  />
                </div>
              </div>

              <div className="space-y-4 pt-2">
                <Button
                  type="submit"
                  className="w-full h-14 bg-blue-600 text-white rounded-2xl font-bold shadow-xl shadow-blue-100 hover:bg-blue-700 transition-all"
                  disabled={loading || formData.otp.length !== 6}
                  data-testid="reset-password-btn"
                >
                  {loading ? <Loader2 className="animate-spin" /> : "Update Password"}
                </Button>
                
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full h-12 text-slate-500 font-bold hover:bg-slate-50 rounded-xl"
                  onClick={() => setStep(1)}
                >
                  <ArrowLeft size={16} className="mr-2" /> Change Email
                </Button>
              </div>
            </form>
          )}
        </div>

        {/* Footer */}
        <footer className="px-10 py-10 mt-auto bg-white border-t border-slate-50 z-20">
          <div className="text-center">
             <p className="text-sm text-slate-500 font-medium">
               Remembered your password? <Link to="/login" className="text-blue-600 font-bold ml-1 hover:underline">Login</Link>
             </p>
          </div>
        </footer>
      </div>
    </div>
  );
};